import moment from 'moment';

export const isSameCalendarDay = (a, b) => {
  if (!a || !b) return false;
  return moment(a).format('YYYY-MM-DD') === moment(b).format('YYYY-MM-DD');
};

export const get12HourParts = (date) => {
  if (!date || !moment(date).isValid()) {
    return { hour12: 12, minutes: 0, isPM: false };
  }
  const m = moment(date);
  const hour24 = m.hour();
  const isPM = hour24 >= 12;
  let hour12 = hour24 % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour12, minutes: m.minute(), isPM };
};

export const apply12HourParts = (date, hour12, minutes, isPM) => {
  const base = moment(date).isValid() ? moment(date) : moment();
  let hour24 = hour12 % 12;
  if (isPM) hour24 += 12;
  return base.clone().hour(hour24).minute(minutes).second(0).millisecond(0).toDate();
};

export const snapToInterval = (minutes, interval = 30) => {
  if (!interval || interval <= 0) return minutes;
  return Math.round(minutes / interval) * interval;
};

export const getMinuteOptions = (interval = 30) => {
  const safeInterval = interval > 0 ? interval : 30;
  const options = [];
  for (let m = 0; m < 60; m += safeInterval) {
    options.push(m);
  }
  return options;
};

export const getAllMinuteOptions = () =>
  Array.from({ length: 60 }, (_, index) => index);

export const getClockCoordinates = (index, total, radius, centerX, centerY) => {
  const angle = (Math.PI / 2) - ((2 * Math.PI * index) / total);
  return {
    x: centerX + radius * Math.cos(angle),
    y: centerY - radius * Math.sin(angle),
  };
};

const getDayBounds = (date) => ({
  min: moment(date).startOf('day').toDate(),
  max: moment(date).endOf('day').toDate(),
});

export const clampDateTime = (date, minDate, maxDate, minTime, maxTime) => {
  if (!date || !moment(date).isValid()) return date;

  let result = moment(date);

  if (minDate && moment(minDate).isValid() && result.isBefore(moment(minDate), 'day')) {
    result = moment(minDate).startOf('day');
  }

  if (maxDate && moment(maxDate).isValid() && result.isAfter(moment(maxDate), 'day')) {
    result = moment(maxDate).startOf('day');
  }

  const current = result.toDate();

  if (minTime && isSameCalendarDay(current, minTime) && result.isBefore(moment(minTime))) {
    result = moment(minTime);
  }

  if (maxTime && isSameCalendarDay(current, maxTime) && result.isAfter(moment(maxTime))) {
    result = moment(maxTime);
  }

  return result.toDate();
};

export const isDateOutOfRange = (date, minDate, maxDate) => {
  if (!date || !moment(date).isValid()) return true;
  const m = moment(date);
  if (minDate && moment(minDate).isValid() && m.isBefore(moment(minDate), 'day')) {
    return true;
  }
  if (maxDate && moment(maxDate).isValid() && m.isAfter(moment(maxDate), 'day')) {
    return true;
  }
  return false;
};

export const isTimeOutOfRange = (date, minTime, maxTime) => {
  if (!date || !moment(date).isValid()) return false;
  const m = moment(date);
  if (minTime && isSameCalendarDay(date, minTime) && m.isBefore(moment(minTime))) {
    return true;
  }
  if (maxTime && isSameCalendarDay(date, maxTime) && m.isAfter(moment(maxTime))) {
    return true;
  }
  return false;
};

export const getEffectiveMinTime = (date, minTime) => {
  if (!minTime) return getDayBounds(date || new Date()).min;
  if (isSameCalendarDay(date, minTime)) return minTime;
  return getDayBounds(date || new Date()).min;
};

export const getEffectiveMaxTime = (date, maxTime) => {
  if (!maxTime) return getDayBounds(date || new Date()).max;
  if (isSameCalendarDay(date, maxTime)) return maxTime;
  return getDayBounds(date || new Date()).max;
};
